import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Loader2, PlusCircle } from 'lucide-react'

interface NoteDialogProps {
  _orderId: string
  existingNotes?: string
  onAddNote: (_orderId: string, _note: string) => void
  isSubmitting?: boolean
  trigger?: React.ReactNode
  open?: boolean
  onOpenChange?: (_open: boolean) => void
}

export const NoteDialog: React.FC<NoteDialogProps> = ({
  _orderId,
  existingNotes,
  onAddNote,
  isSubmitting = false,
  trigger,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const [note, setNote] = useState('')
  
  // Use controlled open if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (note.trim()) {
      onAddNote(_orderId, note.trim())
      if (!isSubmitting) {
        setNote('')
        // Only close if we're controlling the state internally
        if (controlledOpen === undefined) {
          setInternalOpen(false)
        } else {
          onOpenChange?.(false)
        }
      }
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen || !isSubmitting) {
      // Only update internal state if we're not controlled
      if (controlledOpen === undefined) {
        setInternalOpen(newOpen)
      } else {
        onOpenChange?.(newOpen)
      }
      if (!newOpen) {
        setNote('')
      }
    }
  }

  // Reset note when dialog opens
  useEffect(() => {
    if (open) {
      setNote('')
    }
  }, [open])

  const defaultTrigger = (
    <Button variant="ghost" size="sm" className="h-8 px-2">
      <PlusCircle className="h-4 w-4 mr-1" />
      Add Note
    </Button>
  )

  // If we have a trigger, render with DialogTrigger
  if (trigger || controlledOpen === undefined) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          {trigger || defaultTrigger}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Add Note</DialogTitle>
              <DialogDescription>
                Add a note to this order. The note will be appended to any existing notes.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              {existingNotes && (
                <div className="space-y-2">
                  <Label htmlFor="existing-notes">Existing Notes</Label>
                  <div className="p-3 bg-gray-100 rounded-md text-sm">
                    <pre className="whitespace-pre-wrap">{existingNotes}</pre>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="note">New Note</Label>
                <Textarea
                  id="note"
                  placeholder="Enter your note here..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="min-h-[100px]"
                  required
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!note.trim() || isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Note'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    )
  }

  // If no trigger and controlled open, render just the Dialog
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Add a note to this order. The note will be appended to any existing notes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {existingNotes && (
              <div className="space-y-2">
                <Label htmlFor="existing-notes">Existing Notes</Label>
                <div className="p-3 bg-gray-100 rounded-md text-sm">
                  <pre className="whitespace-pre-wrap">{existingNotes}</pre>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="note">New Note</Label>
              <Textarea
                id="note"
                placeholder="Enter your note here..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="min-h-[100px]"
                required
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!note.trim() || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Note'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
